import React from "react";
import { Card } from "@/components/ui/Card";
import { FiltrationInfo ,Dashboard} from "@/types/dashboard";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import { FiAlertTriangle, FiAlertCircle } from "react-icons/fi";
import { isVSDPump } from "@/utils/utils";
import { Colors } from "@/constants/colors";
import { isPrimePressureSupported } from "@/utils/firmware";

interface FiltrationCardProps {
  dashboard: Dashboard;
  filtrationData: FiltrationInfo;
  pumpType?: string;
  firmwareVersion?: string;
}

const FiltrationCard: React.FC<FiltrationCardProps> = ({
  dashboard,
  filtrationData,
  pumpType,
  firmwareVersion,
}) => {
  const isVSD = isVSDPump(pumpType || null);
  const usePrimePressure = isPrimePressureSupported(firmwareVersion);

  const safeBackwashPressure = dashboard.backwash_pressure ?? 0;
  const safeMainPumpStatus = dashboard.main_pump_status ?? 0;
  const safeFlowRate = dashboard.actual_flow_rate ?? filtrationData.flow_rate ?? 0;
  const warningPressure = usePrimePressure ? (dashboard.prime_pressure ?? 0) : (dashboard.actual_pressure ?? 0);
  const displayPressure = usePrimePressure ? (dashboard.prime_pressure ?? filtrationData.pressure ?? 0) : (filtrationData.pressure ?? 0);

  const computeFilterHealth = (): { percent: number; color: string } => {
    const alert = safeBackwashPressure;
    if (usePrimePressure) {
      const isPumpOff = safeMainPumpStatus === 0;
      const actualP = dashboard.prime_pressure ?? 0;
      if (isPumpOff || alert <= 0) {
        return { percent: 0, color: "#22c55e" };
      }
      const start = alert - 50;
      if (actualP < start) {
        return { percent: 5, color: "#22c55e" };
      }
      const clamped = Math.min(alert, Math.max(start, actualP));
      const progress = Math.min(1, Math.max(0, (clamped - start) / 50));
      const color = clamped <= alert - 25 ? "#22c55e" : clamped <= alert - 15 ? "#f59e0b" : "#ef4444";
      return { percent: Math.round(progress * 100), color };
    }

    const mid = alert / 2;
    const actual = dashboard.actual_pressure ?? 0;
    let progress = 0;
    if (actual >= 0 && actual <= mid) {
      progress = 1;
    } else if (mid !== 0) {
      progress = 1 - (actual - mid) / mid;
    } else {
      progress = 0;
    }
    if (actual === 0) {
      progress = 0;
    }
    progress = Math.min(1, Math.max(0, progress));
    const color = progress <= 0.2 ? "#ef4444" : progress <= 0.4 ? "#f59e0b" : "#22c55e";
    return { percent: Math.round(progress * 100), color };
  };

  // 根据流量、压力和泵状态显示警告
  const getWarningMessage = () => {
    const flowRate = safeFlowRate;
    const pressure = warningPressure;
    const pumpStatus = safeMainPumpStatus;

    if (flowRate < 10 && pressure > 10 && pumpStatus !== 0) {
      return { title: "Abnormal Flow", message: "Please check that flow sensor turbine is operational and not obstructed.", isRed: false };
    } else if (flowRate > 10 && pressure < 10 && pumpStatus !== 0) {
      return { title: "Abnormal Pressure", message: "Please check pressure sensor airline is connected and clear of any debris", isRed: false };
    } else if (flowRate < 10 && pressure < 10 && pumpStatus !== 0) {
      return { title: "Pump Running Dry", message: "Check the water level of pool or if the filter pump is operational", isRed: true };
    }
    return null;
  };

  const warningInfo = getWarningMessage();
  const filterHealth = computeFilterHealth();

  return (
    <Card>
      <div className="p-4">
        <h5 className="font-semibold mb-4">Filtration</h5>

        {filtrationData.alert && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 rounded-lg flex items-center">
            <FiAlertTriangle className="text-red-500 mr-2 text-lg" />
            <p className="text-red-700 dark:text-red-300 text-sm">
              {filtrationData.alert}
            </p>
          </div>
        )}
        
        {warningInfo && (
          <div className={`mb-4 p-3 rounded-lg flex items-center ${warningInfo.isRed ? 'bg-red-100 dark:bg-red-900/30' : 'bg-yellow-100 dark:bg-yellow-900/30'}`}>
            <FiAlertCircle className={`${warningInfo.isRed ? 'text-red-500' : 'text-yellow-500'} mr-2 text-lg`} />
            <div>
              <p className={`${warningInfo.isRed ? 'text-red-700 dark:text-red-300' : 'text-yellow-700 dark:text-yellow-300'} text-sm font-medium`}>
                {warningInfo.title}
              </p>
              <p className={`${warningInfo.isRed ? 'text-red-600 dark:text-red-400' : 'text-yellow-600 dark:text-yellow-400'} text-xm`}>
                {warningInfo.message}
              </p>
            </div>
          </div>
        )}

        {/* 非VSD显示2个圆形指标，VSD显示6个指标分成两行，每行3个 */}
        <div className={`grid ${isVSD ? "grid-cols-3" : "grid-cols-2"} gap-4`}>
          {/* Flow Rate - Always shown */}
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 mb-2">
              <CircularProgressbar
                value={filtrationData.flow_rate}
                maxValue={200}
                text={`${filtrationData.flow_rate} lpm`}
                styles={buildStyles({
                  textSize: "20px",
                  pathColor: Colors.AQUA_MARINE,
                  textColor: Colors.COOL_BLUE,
                  trailColor: Colors.WHITE_GRAY,
                })}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Flow Rate
            </p>
          </div>

          {/* Pressure - Always shown */}
          <div className="flex flex-col items-center">
            <div className="w-20 h-20 mb-2">
              <CircularProgressbar
                value={displayPressure}
                maxValue={100}
                text={`${displayPressure} kPa`}
                styles={buildStyles({
                  textSize: "20px",
                  pathColor: Colors.AQUA_MARINE,
                  textColor: Colors.COOL_BLUE,
                  trailColor: Colors.WHITE_GRAY,
                })}
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">Pressure</p>
          </div>

          {/* VSD specific metrics - first row */}
          {isVSD && (
            <>
              {/* Power */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 mb-2">
                  <CircularProgressbar
                    value={filtrationData.power}
                    maxValue={1500}
                    text={`${filtrationData.power} W`}
                    styles={buildStyles({
                      textSize: "20px",
                      pathColor: Colors.AQUA_MARINE,
                      textColor: Colors.COOL_BLUE,
                      trailColor: Colors.WHITE_GRAY,
                    })}
                  />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Power
                </p>
              </div>

              {/* Current */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 mb-2">
                  <CircularProgressbar
                    value={filtrationData.current / 100}
                    maxValue={30}
                    text={`${filtrationData.current / 100} A`}
                    styles={buildStyles({
                      textSize: "20px",
                      pathColor: Colors.AQUA_MARINE,
                      textColor: Colors.COOL_BLUE,
                      trailColor: Colors.WHITE_GRAY,
                    })}
                  />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Current
                </p>
              </div>

              {/* Pump Speed */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 mb-2">
                  <CircularProgressbar
                    value={filtrationData.pump_speed}
                    maxValue={100}
                    text={`${filtrationData.pump_speed} %`}
                    styles={buildStyles({
                      textSize: "20px",
                      pathColor: Colors.AQUA_MARINE,
                      textColor: Colors.COOL_BLUE,
                      trailColor: Colors.WHITE_GRAY,
                    })}
                  />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Pump Speed
                </p>
              </div>

              {/* Drive Temp */}
              <div className="flex flex-col items-center">
                <div className="w-20 h-20 mb-2">
                  <CircularProgressbar
                    value={filtrationData.drive_temp}
                    maxValue={100}
                    text={`${filtrationData.drive_temp} °C`}
                    styles={buildStyles({
                      textSize: "20px",
                      pathColor: Colors.AQUA_MARINE,
                      textColor: Colors.COOL_BLUE,
                      trailColor: Colors.WHITE_GRAY,
                    })}
                  />
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Drive Temp
                </p>
              </div>
            </>
          )}
        </div>
        {/* Filter Health - Always shown as a bar */}
        <div className="col-span-2 mt-4">
          <div className="flex justify-between mb-1">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Filter Health
            </p>
            <p className="text-sm font-medium">
              {filterHealth.percent}%
            </p>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
            <div
              className="h-2.5 rounded-full"
              style={{
                width: `${filterHealth.percent}%`,
                backgroundColor: filterHealth.color,
              }}
            ></div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default FiltrationCard;
